import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import Cookies from "js-cookie";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FiAlertTriangle, FiCheck, FiCheckCircle, FiDownload, FiList } from "react-icons/fi";
import TableSkeleton from "../../components/call-center/listing/TableSkeleton";
import ListingEmptyState from "../../components/call-center/listing/ListingEmptyState";
import ListingPaginationFooter from "../../components/call-center/listing/ListingPaginationFooter";
import ListingFilterHeader from "../../components/call-center/listing/ListingFilterHeader";
import StatusBadge from "../../components/call-center/listing/StatusBadge";
import {
  FILTER_FIELD_CLASS,
  FILTER_LABEL_CLASS,
  LISTING_CARD_CLASS,
  LISTING_CONTAINER_CLASS,
  LISTING_FILTER_CARD_CLASS,
  LISTING_ICON_ACTION_CLASS,
  LISTING_PAGE_CLASS,
} from "../../components/call-center/listing/listingStyles";
import useCallCenterT from "../../hooks/useCallCenterT";
import { listWeeklyTrackerAgents } from "../../service/CallCenterAnalytics";
import {
  acknowledgeAlert,
  getAlertReport,
  getAlertSummary,
  listAlertEvents,
  listAlerts,
  resolveAlert,
} from "../../service/CallCenterAlerts";
import { hasScreenAccess } from "../../utils/permissions";
import { isCallingEnabled } from "../../utils/orgFeatures";
import { processAgentAlertNotifications } from "../../utils/agentAlertNotifications";
import { formatAlertDuration } from "../../utils/alertDuration";

const STATUS_OPTIONS = [
  "",
  "PENDING",
  "WARNING",
  "ESCALATED_L1",
  "ESCALATED_L2",
  "ACKNOWLEDGED",
  "RESOLVED",
];

const formatEventTime = (value) => {
  if (!value) return "—";
  return moment(value).format("MMM D, YYYY h:mm A");
};

const alertLink = (alert) => {
  if (alert.itemType === "TICKET" && alert.referenceId) {
    const params = new URLSearchParams({ ticketId: alert.referenceId });
    if (alert.referenceNumber) {
      params.set("accountNo", alert.referenceNumber);
    }
    return `/call-center/tickets?${params.toString()}`;
  }
  if (alert.itemType === "BULK_ASSIGNMENT") {
    const params = new URLSearchParams();
    if (alert.agentUserId) {
      params.set("assigneeId", String(alert.agentUserId));
    }
    const query = params.toString();
    return query ? `/call-center/tickets?${query}` : "/call-center/tickets";
  }
  if (alert.itemType === "MISSED_CALL" && isCallingEnabled()) {
    return "/call-center/browser-phone";
  }
  return "/call-center/alerts";
};

const CallCenterAlertsPage = () => {
  const t = useCallCenterT();
  const [alerts, setAlerts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [resolveNote, setResolveNote] = useState("");
  const [reassignToUserId, setReassignToUserId] = useState("");
  const [resolveTarget, setResolveTarget] = useState(null);
  const [ackTarget, setAckTarget] = useState(null);
  const [ackNote, setAckNote] = useState("");
  const [expandedAlertId, setExpandedAlertId] = useState(null);
  const [alertEvents, setAlertEvents] = useState({});
  const [eventsLoadingId, setEventsLoadingId] = useState(null);
  const [reportRange, setReportRange] = useState({
    startDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });
  const [report, setReport] = useState(null);

  const currentUserId = Number(Cookies.get("userId") || 0);
  const canMonitor = hasScreenAccess("CC_AGENT_ALERTS", "canView");
  const canResolve = hasScreenAccess("CC_AGENT_ALERTS", "canUpdate");

  const loadAgents = useCallback(async () => {
    try {
      const data = await listWeeklyTrackerAgents();
      setAgents(Array.isArray(data) ? data : []);
    } catch {
      setAgents([]);
    }
  }, []);

  const loadAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const [data, summary] = await Promise.all([
        listAlerts({
          page,
          size: 25,
          status: statusFilter || undefined,
          agentUserId: agentFilter ? Number(agentFilter) : undefined,
          openOnly: statusFilter ? true : false,
        }),
        getAlertSummary(),
      ]);
      const items = data?.items || [];
      setAlerts(items);
      setTotalPages(data?.totalPages || 0);
      setTotalElements(data?.totalElements || 0);
      processAgentAlertNotifications(items, summary, t);
    } catch (error) {
      toast.error(error.message || t("ccAlertsLoadFailed"));
    } finally {
      setLoading(false);
    }
  }, [agentFilter, page, statusFilter, t]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  useEffect(() => {
    loadAlerts();
    const timer = window.setInterval(loadAlerts, 30000);
    return () => window.clearInterval(timer);
  }, [loadAlerts]);

  const toggleTimeline = async (alertId) => {
    if (expandedAlertId === alertId) {
      setExpandedAlertId(null);
      return;
    }
    setExpandedAlertId(alertId);
    if (alertEvents[alertId]) return;
    try {
      setEventsLoadingId(alertId);
      const events = await listAlertEvents(alertId);
      setAlertEvents((prev) => ({ ...prev, [alertId]: Array.isArray(events) ? events : [] }));
    } catch (error) {
      toast.error(error.message || t("ccAlertsLoadFailed"));
    } finally {
      setEventsLoadingId(null);
    }
  };

  const handleAcknowledge = async () => {
    if (!ackTarget) return;
    try {
      await acknowledgeAlert(ackTarget.id, ackNote.trim() || undefined);
      toast.success(t("ccAlertAcknowledged"));
      setAckTarget(null);
      setAckNote("");
      loadAlerts();
    } catch (error) {
      toast.error(error.message || t("ccAlertAckFailed"));
    }
  };

  const handleResolve = async () => {
    if (!resolveTarget) return;
    try {
      await resolveAlert(resolveTarget.id, {
        resolutionNote: resolveNote,
        reassignToUserId: reassignToUserId ? Number(reassignToUserId) : undefined,
      });
      toast.success(t("ccAlertResolved"));
      setResolveTarget(null);
      setResolveNote("");
      setReassignToUserId("");
      loadAlerts();
    } catch (error) {
      toast.error(error.message || t("ccAlertResolveFailed"));
    }
  };

  const loadReport = async () => {
    try {
      const data = await getAlertReport(reportRange);
      setReport(data);
    } catch (error) {
      toast.error(error.message || t("ccAlertReportFailed"));
    }
  };

  const exportReportCsv = () => {
    if (!report?.byAgent?.length) {
      toast.info(t("ccAlertReportEmpty"));
      return;
    }
    const header = "Agent,Alerts,Resolved,Escalated,Acknowledged,Avg Response (sec)\n";
    const rows = report.byAgent
      .map(
        (row) =>
          `"${row.agentName || row.agentUserId}",${row.alertCount},${row.resolvedCount},${row.escalatedCount},${row.acknowledgedCount},${row.avgResponseSeconds ?? ""}`
      )
      .join("\n");
    const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `agent-alerts-report-${reportRange.startDate}-${reportRange.endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const agentNameById = useMemo(() => {
    const map = new Map();
    agents.forEach((agent) => map.set(agent.id, agent.name));
    return map;
  }, [agents]);

  const durationColumnLabel = statusFilter === "RESOLVED"
    ? t("ccAlertResponseTime")
    : statusFilter
      ? t("ccAlertOpenFor")
      : t("ccAlertDuration");

  const alertStatusLabel = (status) => (status ? status.replace(/_/g, " ") : "—");

  const resetFilters = () => {
    setStatusFilter("");
    setAgentFilter("");
    setPage(0);
  };

  const hasActiveFilters = Boolean(statusFilter || agentFilter);

  if (!canMonitor) {
    return (
      <div className="p-6">
        <p className="text-sm text-slate-600">{t("ccAlertsAccessDenied")}</p>
      </div>
    );
  }

  return (
    <div className={LISTING_PAGE_CLASS}>
      <div className={LISTING_CONTAINER_CLASS}>
      <header className="mb-6">
        <h1 className="text-[28px] font-bold tracking-tight text-[#111827]">{t("ccAlertsPageTitle")}</h1>
        <p className="mt-1 text-sm text-[#6b7280]">{t("ccAlertsPageSubtitle")}</p>
      </header>

      <section className={LISTING_FILTER_CARD_CLASS}>
        <ListingFilterHeader
          title={t("ccListFiltersTitle")}
          page={page}
          totalPages={totalPages}
          totalElements={totalElements}
          resultsLabel={t("ccAlertsResultsLabel")}
          showingPageLabel={t("ccShowingPage")}
          ofLabel={t("ccOf")}
          refreshLabel={t("ccRefresh")}
          loading={loading}
          onRefresh={loadAlerts}
        />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-12">
          <div className="xl:col-span-4">
            <label className={FILTER_LABEL_CLASS}>{t("ccAlertStatus")}</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setPage(0);
                setStatusFilter(e.target.value);
              }}
              className={FILTER_FIELD_CLASS}
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status || "all"} value={status}>
                  {status ? alertStatusLabel(status) : t("ccAllStatuses")}
                </option>
              ))}
            </select>
          </div>
          <div className="xl:col-span-4">
            <label className={FILTER_LABEL_CLASS}>{t("ccAlertAgent")}</label>
            <select
              value={agentFilter}
              onChange={(e) => {
                setPage(0);
                setAgentFilter(e.target.value);
              }}
              className={FILTER_FIELD_CLASS}
            >
              <option value="">{t("ccAllAgents")}</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2 xl:col-span-4">
            <button
              type="button"
              onClick={resetFilters}
              disabled={!hasActiveFilters}
              className="h-10 flex-1 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("ccClearFilters")}
            </button>
            <Link
              to="/call-center/sla-settings"
              className="inline-flex h-10 shrink-0 items-center rounded-xl border border-slate-200 px-4 text-sm font-medium text-[#2B7FFF] hover:bg-slate-50"
            >
              {t("ccSlaSettingsLink")}
            </Link>
          </div>
        </div>
      </section>

      <section className={`mb-6 ${LISTING_CARD_CLASS}`}>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="border-b border-slate-100 bg-slate-50/80 text-left">
              <tr>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{t("ccAlertAgent")}</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{t("ccAlertReference")}</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{t("ccAlertReason")}</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{t("ccAlertPriority")}</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{durationColumnLabel}</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{t("ccAlertStatus")}</th>
                <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">{t("ccActions")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton rows={5} columns={7} />
              ) : alerts.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <ListingEmptyState
                      icon={FiAlertTriangle}
                      title={t("ccAlertsEmptyTitle")}
                      hint={t("ccAlertsEmptyHint")}
                    />
                  </td>
                </tr>
              ) : (
                alerts.map((alert) => {
                  const isOwn = alert.agentUserId === currentUserId;
                  const canAck =
                    isOwn && alert.status !== "ACKNOWLEDGED" && alert.status !== "RESOLVED";
                  const canResolveRow = canResolve && alert.status !== "RESOLVED";
                  const isExpanded = expandedAlertId === alert.id;
                  const events = alertEvents[alert.id] || [];
                  return (
                    <React.Fragment key={alert.id}>
                      <tr className="border-t border-slate-100 transition hover:bg-slate-50/70">
                        <td className="px-4 py-3 font-medium text-slate-800">
                          {alert.agentName || agentNameById.get(alert.agentUserId) || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <Link to={alertLink(alert)} className="text-[#2B7FFF] hover:underline">
                            {alert.referenceNumber || alert.referenceId}
                          </Link>
                        </td>
                        <td className="px-4 py-3">{alert.reason?.replace(/_/g, " ") || "—"}</td>
                        <td className="px-4 py-3">{alert.priority || "—"}</td>
                        <td className="px-4 py-3" title={alert.status === "RESOLVED" ? t("ccAlertResponseTime") : t("ccAlertOpenFor")}>
                          {formatAlertDuration(alert.pendingDurationSeconds)}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={alert.status} label={alertStatusLabel(alert.status)} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => toggleTimeline(alert.id)}
                              title={isExpanded ? t("ccHideTimeline") : t("ccViewTimeline")}
                              className={LISTING_ICON_ACTION_CLASS}
                            >
                              <FiList className="h-4 w-4" />
                            </button>
                            {canAck && (
                              <button
                                type="button"
                                onClick={() => {
                                  setAckTarget(alert);
                                  setAckNote("");
                                }}
                                title={t("ccAcknowledge")}
                                className={LISTING_ICON_ACTION_CLASS}
                              >
                                <FiCheck className="h-4 w-4" />
                              </button>
                            )}
                            {canResolveRow && (
                              <button
                                type="button"
                                onClick={() => {
                                  setResolveTarget(alert);
                                  setResolveNote("");
                                  setReassignToUserId("");
                                }}
                                title={t("ccResolve")}
                                className={LISTING_ICON_ACTION_CLASS}
                              >
                                <FiCheckCircle className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-t border-slate-50 bg-slate-50/60">
                          <td colSpan={7} className="px-4 py-4">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                              {t("ccAlertTimeline")}
                            </p>
                            {eventsLoadingId === alert.id ? (
                              <p className="text-sm text-slate-500">{t("ccAlertsLoading")}</p>
                            ) : events.length === 0 ? (
                              <p className="text-sm text-slate-500">{t("ccAlertTimelineEmpty")}</p>
                            ) : (
                              <ul className="space-y-2">
                                {events.map((event) => (
                                  <li
                                    key={event.id}
                                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                                  >
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <span className="font-medium">
                                        {event.eventType?.replace(/_/g, " ") || "Event"}
                                      </span>
                                      <span className="text-xs text-slate-400">
                                        {formatEventTime(event.createdAt)}
                                      </span>
                                    </div>
                                    {event.metadataJson?.note && (
                                      <p className="mt-1 text-xs text-slate-500">{event.metadataJson.note}</p>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <ListingPaginationFooter
          totalElements={totalElements}
          resultsLabel={t("ccAlertsResultsLabel")}
          page={page}
          totalPages={totalPages}
          loading={loading}
          onPrevious={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => p + 1)}
          previousLabel={t("ccPrevious")}
          nextLabel={t("ccNext")}
        />
      </section>

      <section className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-base font-semibold text-slate-800">{t("ccAlertReportTitle")}</h2>
        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-4">
          <input
            type="date"
            value={reportRange.startDate}
            onChange={(e) => setReportRange((prev) => ({ ...prev, startDate: e.target.value }))}
            className="h-10 rounded-xl border border-slate-200 px-3 text-sm"
          />
          <input
            type="date"
            value={reportRange.endDate}
            onChange={(e) => setReportRange((prev) => ({ ...prev, endDate: e.target.value }))}
            className="h-10 rounded-xl border border-slate-200 px-3 text-sm"
          />
          <button
            type="button"
            onClick={loadReport}
            className="h-10 rounded-xl bg-[#2B7FFF] px-4 text-sm font-medium text-white"
          >
            {t("ccLoadReport")}
          </button>
          <button
            type="button"
            onClick={exportReportCsv}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 text-sm font-medium text-slate-700"
          >
            <FiDownload className="h-4 w-4" />
            {t("ccExportCsv")}
          </button>
        </div>
        {report && (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            {[
              ["ccReportCreated", report.totalCreated],
              ["ccReportResolved", report.totalResolved],
              ["ccReportAutoResolved", report.totalAutoResolved],
              ["ccReportEscalatedL1", report.totalEscalatedL1],
              ["ccReportEscalatedL2", report.totalEscalatedL2],
              ["ccReportAcknowledged", report.totalAcknowledged],
            ].map(([key, value]) => (
              <div key={key} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-400">{t(key)}</p>
                <p className="mt-1 text-xl font-semibold text-slate-800">{value ?? 0}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {ackTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">{t("ccAcknowledgeAlert")}</h3>
            <p className="mt-1 text-sm text-slate-500">{ackTarget.referenceNumber}</p>
            <textarea
              value={ackNote}
              onChange={(e) => setAckNote(e.target.value)}
              rows={4}
              className="mt-4 w-full rounded-xl border border-slate-200 p-3 text-sm"
              placeholder={t("ccAckNotePlaceholder")}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setAckTarget(null);
                  setAckNote("");
                }}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm"
              >
                {t("ccCancel")}
              </button>
              <button
                type="button"
                onClick={handleAcknowledge}
                className="rounded-xl bg-[#2B7FFF] px-4 py-2 text-sm font-medium text-white"
              >
                {t("ccAcknowledge")}
              </button>
            </div>
          </div>
        </div>
      )}

      {resolveTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">{t("ccResolveAlert")}</h3>
            <p className="mt-1 text-sm text-slate-500">{resolveTarget.referenceNumber}</p>
            {resolveTarget.itemType === "TICKET" && canResolve && (
              <label className="mt-4 block text-sm">
                <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-400">
                  {t("ccReassignToAgent")}
                </span>
                <select
                  value={reassignToUserId}
                  onChange={(e) => setReassignToUserId(e.target.value)}
                  className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                >
                  <option value="">{t("ccNoReassign")}</option>
                  {agents
                    .filter((agent) => agent.id !== resolveTarget.agentUserId)
                    .map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name}
                      </option>
                    ))}
                </select>
              </label>
            )}
            <textarea
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
              rows={4}
              className="mt-4 w-full rounded-xl border border-slate-200 p-3 text-sm"
              placeholder={t("ccResolutionNotePlaceholder")}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setResolveTarget(null);
                  setResolveNote("");
                  setReassignToUserId("");
                }}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm"
              >
                {t("ccCancel")}
              </button>
              <button
                type="button"
                onClick={handleResolve}
                className="rounded-xl bg-[#2B7FFF] px-4 py-2 text-sm font-medium text-white"
              >
                {t("ccResolve")}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default CallCenterAlertsPage;
